import { FC, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgRelinkCampaignResult } from 'services/api/paymentGateway/types';
import { PgFields } from '../common/PgFields';

type TRelinkCampaignDialogProps = {
	open: boolean;
	onClose: () => void;
};

const ResultFields: FC<{ result: TPgRelinkCampaignResult }> = ({ result }) => (
	<PgFields
		minWidth={120}
		items={[
			['Customers', result.customers],
			['Sent', result.sent],
			['Skipped', result.skipped],
			['Failed', result.failed],
		]}
	/>
);

/**
 * Re-link campaign (03 §4.11): the `pg-relink-required` e-mail to customers who must add their card
 * again after the move to the new provider. A dry run counts the customers; the real run needs a
 * separate confirmation.
 */
export const RelinkCampaignDialog: FC<TRelinkCampaignDialogProps> = ({ open, onClose }) => {
	const [busy, setBusy] = useState<'dry' | 'real' | null>(null);
	const [dryResult, setDryResult] = useState<TPgRelinkCampaignResult | null>(null);
	const [realResult, setRealResult] = useState<TPgRelinkCampaignResult | null>(null);
	const [confirmed, setConfirmed] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		setBusy(null);
		setDryResult(null);
		setRealResult(null);
		setConfirmed(false);
		setError(null);
	}, [open]);

	const run = async (dryRun: boolean) => {
		setBusy(dryRun ? 'dry' : 'real');
		setError(null);
		try {
			const result = await api.paymentGateway.runRelinkCampaign({ dryRun });
			if (dryRun) setDryResult(result);
			else setRealResult(result);
		} catch (e) {
			setError(getPgErrorMessage(e, 'The campaign failed'));
		} finally {
			setBusy(null);
		}
	};

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle color="primary">Re-link campaign</DialogTitle>
			<DialogContent>
				<Typography variant="body2" sx={{ mb: 2 }}>
					Sends the &quot;add your payment method again&quot; e-mail to every customer who needs to
					re-link and has no active payment method. Start with a dry run: it sends nothing and
					counts the customers.
				</Typography>
				<Typography variant="subtitle2" color="primary">
					1. Dry run
				</Typography>
				<Box sx={{ my: 1 }}>
					{dryResult ? (
						<ResultFields result={dryResult} />
					) : (
						<Button
							variant="outlined"
							size="small"
							onClick={() => void run(true)}
							disabled={!!busy}
							startIcon={busy === 'dry' ? <CircularProgress size={14} /> : undefined}
						>
							Count customers
						</Button>
					)}
				</Box>
				<Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
					2. Send
				</Typography>
				{realResult ? (
					<Box sx={{ my: 1 }}>
						<Alert severity="success" sx={{ mb: 1 }}>
							The campaign is done.
						</Alert>
						<ResultFields result={realResult} />
					</Box>
				) : (
					<Box sx={{ my: 1 }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={confirmed}
									disabled={!dryResult || !!busy}
									onChange={(e) => setConfirmed(e.target.checked)}
								/>
							}
							label={`I want to e-mail ${dryResult?.customers ?? 'these'} customers now`}
						/>
						<Box>
							<Button
								variant="contained"
								color="error"
								onClick={() => void run(false)}
								disabled={!dryResult || !confirmed || !!busy}
								startIcon={busy === 'real' ? <CircularProgress size={16} /> : undefined}
							>
								Send e-mails
							</Button>
						</Box>
					</Box>
				)}
				{error && (
					<Alert severity="error" sx={{ mt: 1 }}>
						{error}
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={!!busy}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};
